/**
 * FileCard — a file the viewer doesn't show inline (Excel, PowerPoint, HEIC
 * outside Safari, a PDF on iOS, or anything that fails to render): its kind
 * icon, name and size. Download is the lightbox's one Download under it
 * (`DownloadLink`), never a second one on the card. `FileLoadFailedCard` is
 * the same card for a file whose bytes didn't arrive: it offers Retry.
 */
import { ArrowClockwise, DownloadSimple } from '../../../../design-system/icons';
import { Button } from '../../../ui/button';
import { FileKindIcon } from '../FileKindIcon';
import { fileMetaLabel } from '../fileKinds';
import { DOWNLOAD_LABEL, LOAD_FAILED_LABEL, RETRY_LABEL, type ViewerItem } from './viewerModel';

const CARD_TESTID = 'file-card';
const LOAD_FAILED_TESTID = 'file-load-failed';
const KIND_ICON_PX = 40;
const DOWNLOAD_ICON_PX = 14;
const RETRY_ICON_PX = 14;

const CARD_CLASS = 'flex w-full max-w-sm flex-col items-center gap-1 rounded-xl bg-card p-8 text-center text-foreground';
const KIND_ICON_CLASS = 'mb-2 text-muted-foreground';
const NAME_CLASS = 'w-full truncate text-body-sm-semibold';
const META_CLASS = 'text-caption-regular text-muted-foreground';
/** The card is white, so its one action is the design system's black pill — never white on white. */
const CARD_ACTION_CLASS =
  'mt-4 h-[30px] rounded-full border-0 bg-zinc-900 px-[14px] text-[12.5px] font-medium text-white shadow-none hover:bg-zinc-800 hover:text-white';

export interface DownloadLinkProps {
  /** The file's object URL; absent while it loads or after it failed, which disables Download. */
  url?: string;
  /** Saved under this name. */
  name: string;
  className?: string;
}

export function DownloadLink({ url, name, className }: DownloadLinkProps) {
  const content = (
    <>
      <DownloadSimple size={DOWNLOAD_ICON_PX} aria-hidden />
      {DOWNLOAD_LABEL}
    </>
  );
  if (!url) {
    return (
      <Button type="button" variant="outline" size="sm" disabled className={className}>
        {content}
      </Button>
    );
  }
  return (
    <Button asChild variant="outline" size="sm" className={className}>
      <a href={url} download={name}>
        {content}
      </a>
    </Button>
  );
}

export interface FileCardProps {
  item: ViewerItem;
}

/** The file's kind icon and name, at the top of either card. */
function FileIdentity({ item }: { item: ViewerItem }) {
  return (
    <>
      <FileKindIcon kind={item.kind} size={KIND_ICON_PX} className={KIND_ICON_CLASS} aria-hidden />
      <p className={NAME_CLASS} title={item.name}>
        {item.name}
      </p>
    </>
  );
}

export function FileCard({ item }: FileCardProps) {
  return (
    <div className={CARD_CLASS} data-testid={CARD_TESTID}>
      <FileIdentity item={item} />
      <p className={META_CLASS}>{fileMetaLabel(item.kind, item.size)}</p>
    </div>
  );
}

export interface FileLoadFailedCardProps {
  item: ViewerItem;
  /** Fetches the file again. */
  onRetry: () => void;
}

export function FileLoadFailedCard({ item, onRetry }: FileLoadFailedCardProps) {
  return (
    <div role="alert" className={CARD_CLASS} data-testid={LOAD_FAILED_TESTID}>
      <FileIdentity item={item} />
      <p className={META_CLASS}>{LOAD_FAILED_LABEL}</p>
      <Button type="button" size="sm" onClick={onRetry} className={CARD_ACTION_CLASS}>
        <ArrowClockwise size={RETRY_ICON_PX} aria-hidden />
        {RETRY_LABEL}
      </Button>
    </div>
  );
}
